import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import SearchableInput from 'components/common/SearchableInput';
import { adminShelfApi, shelfApi } from 'api';
import { toastApiError } from 'utils/apiErrors';

const TRANSFER_MODE = { ALL: 'all', PARTIAL: 'partial' };

const getQuantityError = (value, available) => {
  if (value === '') return null;
  if (!/^\d+$/.test(value)) return 'Please enter a whole number';
  const quantity = Number(value);
  if (quantity < 1) return 'Quantity must be at least 1';
  if (quantity > available) return `Cannot exceed ${available}`;
  return null;
};

const getSuccessMessage = (response, isPartial) => {
  const merged = !!response?.merged;
  if (isPartial) {
    const count = response?.quantity;
    const units = count != null ? `${count} units` : 'Units';
    return merged
      ? `${units} transferred and merged into an existing batch on the destination shelf`
      : `${units} transferred to a new batch on the destination shelf`;
  }
  return merged
    ? 'Batch transferred and merged into an existing batch on the destination shelf'
    : 'Batch transferred successfully';
};

const BatchTransferModal = ({ show, onHide, batch, onSuccess }) => {
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [shelfSearchValue, setShelfSearchValue] = useState('');
  const [mode, setMode] = useState(TRANSFER_MODE.ALL);
  const [quantityInput, setQuantityInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allocated = batch?.quantity_allocated ?? 0;
  // 서버 검증 기준과 동일: 미할당 수량 이하만 부분 이동 가능
  const available = Math.max(0, (batch?.quantity ?? 0) - allocated);
  const isPartial = mode === TRANSFER_MODE.PARTIAL;

  const quantityError = useMemo(
    () => (isPartial ? getQuantityError(quantityInput, available) : null),
    [isPartial, quantityInput, available],
  );
  const isQuantityValid =
    !isPartial || (quantityInput !== '' && !quantityError);

  const resetForm = useCallback(() => {
    setSelectedShelf(null);
    setShelfSearchValue('');
    setMode(TRANSFER_MODE.ALL);
    setQuantityInput('');
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  const handleShelfSelect = useCallback(
    item => {
      if (item.id === batch?.shelf?.id) {
        toast.warning('The batch is already on this shelf');
        return;
      }
      setSelectedShelf(item);
      setShelfSearchValue(item.code);
    },
    [batch?.shelf?.id],
  );

  const handleShelfInputChange = useCallback(
    term => {
      setShelfSearchValue(term);
      if (selectedShelf && term !== selectedShelf.code) {
        setSelectedShelf(null);
      }
    },
    [selectedShelf],
  );

  const handleTransfer = async () => {
    if (!selectedShelf || !isQuantityValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const payload = {
        product_batch_id: batch.id,
        destination_shelf_id: selectedShelf.id,
      };
      if (isPartial) payload.quantity = Number(quantityInput);

      const response = await adminShelfApi.batchTransfer(payload);

      toast.success(getSuccessMessage(response, isPartial));
      closeModal();
      onSuccess();
    } catch (error) {
      toastApiError(error, 'Failed to transfer batch');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : closeModal}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Transfer Batch</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Product</Form.Label>
          <Form.Control
            type="text"
            value={
              batch
                ? `${batch.product?.sku ?? '-'} — ${batch.product?.name ?? '-'}`
                : ''
            }
            disabled
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Batch</Form.Label>
          <Form.Control
            type="text"
            value={
              batch
                ? `${batch.batch_code || '-'} / Expiry: ${
                    batch.expire_date || '-'
                  } / Qty: ${batch.quantity ?? '-'}`
                : ''
            }
            disabled
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Current Shelf</Form.Label>
          <Form.Control type="text" value={batch?.shelf?.code || ''} disabled />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Transfer Type</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              id="batch-transfer-mode-all"
              name="batch-transfer-mode"
              label="Transfer all"
              checked={!isPartial}
              disabled={isSubmitting}
              onChange={() => setMode(TRANSFER_MODE.ALL)}
            />
            <Form.Check
              inline
              type="radio"
              id="batch-transfer-mode-partial"
              name="batch-transfer-mode"
              label="Transfer partial"
              checked={isPartial}
              disabled={isSubmitting || available === 0}
              onChange={() => setMode(TRANSFER_MODE.PARTIAL)}
            />
          </div>
          <Form.Text className="text-600">
            {available === 0
              ? 'All units are allocated, so only the whole batch can be transferred.'
              : isPartial
              ? 'Allocated units stay on the current batch, which is kept even if its quantity becomes 0.'
              : allocated > 0
              ? `${allocated} allocated unit(s) will move together with the batch.`
              : null}
          </Form.Text>
        </Form.Group>

        {isPartial && (
          <Form.Group className="mb-3">
            <Form.Label>
              Quantity to Transfer<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              value={quantityInput}
              onChange={e => setQuantityInput(e.target.value.trim())}
              isInvalid={!!quantityError}
              disabled={isSubmitting}
              placeholder={`1 - ${available}`}
            />
            <Form.Control.Feedback type="invalid">
              {quantityError}
            </Form.Control.Feedback>
            <Form.Text className="text-600">
              Available to transfer: {available}
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>
            Destination Shelf<span className="text-danger">*</span>
          </Form.Label>
          <SearchableInput
            apiService={shelfApi}
            searchField="code__icontains"
            additionalParams={{ active: 'true' }}
            displayField="code"
            value={shelfSearchValue}
            onChange={handleShelfInputChange}
            onSelect={handleShelfSelect}
            placeholder="Enter 2 or more characters to search shelf..."
            renderItem={item => (
              <div className="font-medium">
                {item?.name} ({item?.code})
              </div>
            )}
            visibleItems={5}
          />
          <Form.Text className="text-600">
            If the destination shelf already has a batch of the same product,
            inventory type and expiry date, the quantities will be merged.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleTransfer}
          disabled={!selectedShelf || !isQuantityValid || isSubmitting}
        >
          {isSubmitting ? 'Transferring...' : 'Transfer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BatchTransferModal;
